import api, { handleApiResponse } from './api';

// ==================== INTERFACES ====================

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  timestamp: string;
}

/**
 * Cliente Persona
 */
export interface PersonCustomer {
  id: number;
  type: 'PERSON';
  cuit: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  address?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Cliente Empresa
 */
export interface CompanyCustomer {
  id: number;
  type: 'COMPANY';
  cuit: string;
  businessName: string;
  companyType?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  legalAddress?: string;
  phone?: string;
  email?: string;
  address?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Tipo unificado de cliente (puede ser persona o empresa)
 */
export type Customer = PersonCustomer | CompanyCustomer;

/**
 * Request para crear cliente persona
 */
export interface CreatePersonCustomerRequest {
  type: 'PERSON';
  cuit: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  address?: string;
}

/**
 * Request para crear cliente empresa
 */
export interface CreateCompanyCustomerRequest {
  type: 'COMPANY';
  cuit: string;
  businessName: string;
  companyType?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  legalAddress?: string;
  phone?: string;
  email?: string;
  address?: string;
}

/**
 * Request unificado para crear cliente
 */
export type CreateCustomerRequest =
  | CreatePersonCustomerRequest
  | CreateCompanyCustomerRequest;

/**
 * Request para actualizar cliente persona (incluye type y cuit para permitir cambio de tipo)
 */
export interface UpdatePersonCustomerRequest {
  type: 'PERSON';
  cuit: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  address?: string;
}

/**
 * Request para actualizar cliente empresa (incluye type y cuit para permitir cambio de tipo)
 */
export interface UpdateCompanyCustomerRequest {
  type: 'COMPANY';
  cuit: string;
  businessName: string;
  companyType?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  legalAddress?: string;
  phone?: string;
  email?: string;
  address?: string;
}

/**
 * Request unificado para actualizar cliente (permite cambiar de persona a empresa y viceversa)
 */
export type UpdateCustomerRequest =
  | UpdatePersonCustomerRequest
  | UpdateCompanyCustomerRequest;

// ==================== SERVICIO ====================

export const customersService = {
  // ==================== CREAR CLIENTES ====================

  /**
   * Crear cliente (persona o empresa)
   * @param request - Datos del cliente a crear
   * @returns Promise con el cliente creado
   */
  async createCustomer(request: CreateCustomerRequest): Promise<Customer> {
    const response = await api.post<ApiResponse<Customer>>(
      '/customers',
      request,
    );
    return handleApiResponse(response);
  },

  // ==================== OBTENER CLIENTES ====================

  /**
   * Obtener todos los clientes (personas y empresas)
   * @returns Promise con la lista de todos los clientes
   */
  async getAllCustomers(): Promise<Customer[]> {
    const response = await api.get<ApiResponse<Customer[]>>('/customers');
    return handleApiResponse(response);
  },

  /**
   * Obtener cliente por ID
   * @param id - ID del cliente a obtener
   * @returns Promise con el cliente encontrado
   */
  async getCustomerById(id: number): Promise<Customer> {
    const response = await api.get<ApiResponse<Customer>>(`/customers/${id}`);
    return handleApiResponse(response);
  },

  /**
   * Obtener cliente por CUIT
   * @param cuit - CUIT del cliente a buscar
   * @returns Promise con el cliente encontrado
   */
  async getCustomerByCuit(cuit: string): Promise<Customer> {
    const response = await api.get<ApiResponse<Customer>>(
      `/customers/cuit/${cuit}`,
    );
    return handleApiResponse(response);
  },

  // ==================== ACTUALIZAR CLIENTES ====================

  /**
   * Actualizar cliente (persona o empresa)
   * @param id - ID del cliente a actualizar
   * @param request - Datos a actualizar
   * @returns Promise con el cliente actualizado
   */
  async updateCustomer(
    id: number,
    request: UpdateCustomerRequest,
  ): Promise<Customer> {
    const response = await api.put<ApiResponse<Customer>>(
      `/customers/${id}`,
      request,
    );
    return handleApiResponse(response);
  },

  // ==================== CAMBIAR ESTADO DE CLIENTES ====================

  /**
   * Cambiar el estado de un cliente (activar/desactivar)
   * @param id - ID del cliente
   * @param isActive - true para activar, false para desactivar
   * @returns Promise con el cliente actualizado
   */
  async changeCustomerStatus(id: number, isActive: boolean): Promise<Customer> {
    const response = await api.patch<ApiResponse<Customer>>(
      `/customers/${id}/status?isActive=${isActive}`,
    );
    return handleApiResponse(response);
  },
};

export default customersService;
